import {
  createPlant,
  getPlantById,
  deletePlantById,
  getDistrictCustomerSiteInfo,
} from '../../Services/serviceURL';
import {
  buildPlantInfoFromApiPlant,
  findCustomerForPlant,
  GUID_REGEX,
  parseJsonSafely,
  persistPlantApiResponse,
  PlantInfo,
  resolvePlantGuid,
  transformDistrictCustomers,
  CustomerLite,
  Asset,
} from './createPlantHelpers';

export interface LoadedPlantState {
  plantData: Record<string, unknown>;
  plantInfo: PlantInfo;
  assets: Asset[];
  selectedPlantId: string | null;
  selectedCustomer: CustomerLite | null;
  sites: import('./createPlantHelpers').Site[];
}

export interface LoadPlantResult {
  success: boolean;
  state?: LoadedPlantState;
  error?: string;
}

function buildLoadedStateFromPlant(
  plant: {
    customer: string;
    site: string;
    plant: string;
    status: string;
    latitude?: string | null;
    longitude?: string | null;
    assets?: Asset[];
    plantId?: string;
    id?: string;
  },
  validCustomers: CustomerLite[]
): LoadedPlantState {
  const updatedPlantInfo = buildPlantInfoFromApiPlant(plant);
  const assets = Array.isArray(plant.assets) ? (plant.assets as Asset[]) : [];
  const customer = findCustomerForPlant(plant.customer, validCustomers);

  return {
    plantData: plant as Record<string, unknown>,
    plantInfo: updatedPlantInfo,
    assets,
    selectedPlantId: plant.plantId || plant.id || null,
    selectedCustomer: customer ?? null,
    sites: customer?.sites?.length ? customer.sites : [],
  };
}

function buildLoadedStateFromStorage(storedPlantData: string): LoadedPlantState | null {
  const plant = parseJsonSafely<{
    customer: string;
    site: string;
    plant: string;
    status: string;
    latitude?: string;
    longitude?: string;
    plantId?: string;
    id?: string;
  }>(storedPlantData);
  if (!plant) {
    return null;
  }

  return {
    plantData: plant as Record<string, unknown>,
    plantInfo: buildPlantInfoFromApiPlant(plant),
    assets: [],
    selectedPlantId: plant.plantId || plant.id || null,
    selectedCustomer: null,
    sites: [],
  };
}

export async function fetchCustomersList(): Promise<CustomerLite[]> {
  const districtCustomerData = await getDistrictCustomerSiteInfo();
  if (!Array.isArray(districtCustomerData)) {
    return [];
  }
  return transformDistrictCustomers(districtCustomerData);
}

export async function loadPlantForEdit(
  plantId: string,
  validCustomers: CustomerLite[]
): Promise<LoadPlantResult> {
  const guidToUse = resolvePlantGuid(plantId);

  if (guidToUse && GUID_REGEX.test(guidToUse)) {
    return loadPlantFromApi(guidToUse, validCustomers);
  }

  return loadPlantFromStorageFallback(plantId);
}

async function loadPlantFromApi(
  guidToUse: string,
  validCustomers: CustomerLite[]
): Promise<LoadPlantResult> {
  try {
    const plantResponse = await getPlantById(guidToUse);
    const plant = plantResponse.data?.Data;

    if (!plantResponse.data?.Success || !plant) {
      throw new Error('Invalid API response');
    }

    persistPlantApiResponse(plant);
    return {
      success: true,
      state: buildLoadedStateFromPlant(plant, validCustomers),
    };
  } catch {
    return loadPlantFromStorageFallback('', 'Failed to load plant data: No valid ID or stored data available');
  }
}

function loadPlantFromStorageFallback(
  plantId: string,
  defaultError?: string
): LoadPlantResult {
  const storedPlantData = localStorage.getItem('currentPlantData');
  if (!storedPlantData) {
    const error = defaultError ?? `Unable to load plant data: Invalid plant ID format (${plantId})`;
    return { success: false, error };
  }

  const state = buildLoadedStateFromStorage(storedPlantData);
  if (!state) {
    return { success: false, error: 'Failed to load plant data' };
  }

  return { success: true, state };
}

export async function saveNewPlant(newPlantData: {
  customerName: string;
  siteName: string;
  plantName: string;
  plantStatus: string;
  cnxSiteId: number;
  latitude: string;
  longitude: string;
}) {
  return createPlant(newPlantData);
}

export async function removePlant(plantId: string) {
  return deletePlantById(plantId);
}
